import { useQuery } from "@apollo/client";
import { useMemo, useRef } from "react";
import { GET_DIGITAL_TWIN_DATA_BY_IDS_QUERY } from "../../queries/digitalTwinQueries";

const INSTANCE_FIELDS = [
  "ct_instance_id",
  "mri_instance_id",
  "coronal_dx_instance_id",
  "sagittal_dx_instance_id",
];

// Build instance map from digital twins
const buildInstancesMap = digitalTwins => {
  const instancesMap = {};

  digitalTwins.forEach(twin => {
    INSTANCE_FIELDS.forEach(field => {
      if (twin[field]) {
        instancesMap[twin[field]] = field;
      }
    });
  });

  return instancesMap;
};

// Compare two instance maps by content
const instanceMapsEqual = (a, b) => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  return keysA.every(key => a[key] === b[key]);
};

export function useDigitalTwinData(digitalTwinIds) {
  const { data, loading, error, refetch } = useQuery(
    GET_DIGITAL_TWIN_DATA_BY_IDS_QUERY,
    {
      variables: { ids: digitalTwinIds },
      skip: !digitalTwinIds?.length,
      fetchPolicy: "network-only",
      onError: error => {},
    }
  );

  const digitalTwins = useMemo(() => {
    return data?.digitalTwinsByIds || [];
  }, [data]);

  // Step 1: Compute the new map (pure)
  const computedInstances = useMemo(() => {
    return buildInstancesMap(digitalTwins);
  }, [digitalTwins]);

  // Step 2: Stabilize reference - prevents unnecessary cascade when
  // digital twin updates but instance IDs remain unchanged
  const stableInstancesRef = useRef(computedInstances);

  if (!instanceMapsEqual(stableInstancesRef.current, computedInstances)) {
    stableInstancesRef.current = computedInstances;
  }

  return {
    digitalTwins,
    instances: stableInstancesRef.current,
    loading,
    error,
    refreshDigitalTwins: refetch,
  };
}
